import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { formatMetric } from "../visuals/formatMetric";

/**
 * Rows and columns inline in the thread — the right answer whenever every row
 * matters equally, or there are more than ~7 series, or the user will scan for
 * one value.
 *
 * Paging reuses the EXISTING tableId machinery (registerTable / fetchTablePage)
 * rather than a second mechanism: same server round trip the side panel makes,
 * same cache, same restore-after-eviction behaviour.
 *
 * Sorting is client-side over the CURRENT PAGE only — the server API has no
 * sort parameter. That is stated in the UI rather than hidden, because a sort
 * that silently covers 25 of 3,000 rows is a lie about the data.
 */
const isNumeric = value =>
  value != null && value !== "" && Number.isFinite(Number(value));

const DataTableBlock = observer(({ block }) => {
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  useEffect(() => {
    if (block.tableId) {
      copilotStore.registerTable(block);
      const table = copilotStore.tables.get(block.tableId);
      if (table && !table.rows.length && !table.loading) {
        copilotStore.fetchTablePage(block.tableId, 1);
      }
    }
    // Registering is keyed on tableId, not on block identity: the block object
    // is a MobX observable that changes on every patch, and re-registering per
    // patch would refetch page 1 mid-scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.tableId]);

  const table = block.tableId ? copilotStore.tables.get(block.tableId) : null;
  const columns = (table ? table.columns : block.columns) || [];
  // Inline rows (small result sets) skip the server entirely.
  const sourceRows = table && table.rows.length ? table.rows : block.rows || [];
  if (!columns.length || !sourceRows.length) return null;

  const rows = sort.key
    ? [...sourceRows].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        const numeric = isNumeric(av) && isNumeric(bv);
        const cmp = numeric
          ? Number(av) - Number(bv)
          : String(av == null ? "" : av).localeCompare(String(bv == null ? "" : bv));
        return sort.dir === "asc" ? cmp : -cmp;
      })
    : sourceRows;

  const totalCount = table ? table.totalCount : rows.length;
  const pageSize = table ? table.pageSize : rows.length;
  const lastPage = Math.max(1, Math.ceil(totalCount / (pageSize || 1)));
  const currentPage = table ? table.currentPage : 1;
  const paged = lastPage > 1;

  const toggleSort = key =>
    setSort(prev =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  return (
    <div className="cpVisual cpVisual--wide">
      {block.title && <div className="cpVisual__title">{block.title}</div>}

      <div className="cpTable__scroll">
        <table className="cpTable">
          <thead>
            <tr>
              {columns.map(column => (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={
                    sort.key === column.key
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <button
                    type="button"
                    className="cpTable__sort"
                    onClick={() => toggleSort(column.key)}
                  >
                    <span>{column.label}</span>
                    <span className="cpTable__sortGlyph" aria-hidden="true">
                      {sort.key === column.key ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || index}>
                {columns.map(column => {
                  const value = row[column.key];
                  return (
                    <td key={column.key}>
                      {isNumeric(value) && column.numeric
                        ? formatMetric(value, { unit: column.unit })
                        : value == null || value === ""
                          ? "—"
                          : String(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cpTable__foot">
        <span className="cpTable__count">
          {paged
            ? `${formatMetric(totalCount)} records · page ${currentPage} of ${lastPage}`
            : `${formatMetric(totalCount)} records`}
        </span>

        {paged && (
          <span className="cpTable__pager">
            <button
              type="button"
              className="cpTable__pageBtn"
              disabled={currentPage <= 1 || table.loading}
              onClick={() => copilotStore.fetchTablePage(block.tableId, currentPage - 1)}
            >
              Prev
            </button>
            <button
              type="button"
              className="cpTable__pageBtn"
              disabled={currentPage >= lastPage || table.loading}
              onClick={() => copilotStore.fetchTablePage(block.tableId, currentPage + 1)}
            >
              Next
            </button>
          </span>
        )}
      </div>

      {paged && sort.key && (
        <div className="cpTable__sortNote">
          Sorted within this page only — the server returns rows unsorted.
        </div>
      )}
    </div>
  );
});

export default DataTableBlock;
